/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
/* eslint-disable no-lone-blocks */
import { Button, Empty, Form, Input, Select, Table, Tabs, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import TabPane from 'antd/es/tabs/TabPane'
import Title from 'antd/es/typography/Title'
import dayjs from 'dayjs'
import {
  ArrowBigDownDash,
  ArrowBigUpDash,
  ArrowDownUp,
  Check,
  CheckCheck,
  Download,
  Hourglass,
  Link,
  SquareIcon,
  Train,
  X,
} from 'lucide-react'
import moment from 'moment'
import { FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

import { STATUS_TRANSITIONS } from '@constants'
import {
  useBlockTrainFileTypes,
  useBlocktrainRules,
  useDestinations,
  useDownload,
  useMaterials,
  useSupplierPurchaseOrders,
} from '@hooks'
import {
  BaseFile,
  BlockTrainFileType,
  BlockTrainStatusEnum,
  BlocktrainUpdatePayload,
  Railcar,
  RailcarDetail,
} from '@types'

import ConfirmAuthorized from '../../../components/ConfirmAuthorized'
import ConfirmFinishLoading from '../../../components/ConfirmFinishLoading'
import LoadingSpinner from '../../../components/LoadingSpinner'
import RailcarsTabPane from '../../../components/RailcarsTabPane'
import {
  ActionsCellContainer,
  ContentContainer,
  FlexCol,
  FlexRow,
  // StyledRow,
} from '../../../components/StyledComponents'
import { DATE_KEYS } from '../../../constants/dateKeys'
import useBlocktrain from '../../../hooks/blocktrain/useBlocktrain'
import { useUpdateBlocktrain } from '../../../hooks/blocktrain/useUpdateBlocktrain'
import useTrainCompanies from '../../../hooks/trainCompany/useTrainCompanies'
import useOrigins from '../../../hooks/useOrigins'
import AddBlockTrainFileModal from './AddBlockTrainFileModalSupplier'
import AddRailcarModal from './AddRailcarModalSupplier'
import RailcarModalViewSupplier from './RailcarModalViewSupplier'
import BlocktrainApi from '../../../hooks/blocktrain/api'

const { AUTHORIZED, FINISHED, LOADING, TRANSIT, UNLOADING, WAITING_APPROVAL, CANCELED } =
  BlockTrainStatusEnum

const iconByStatus = {
  [LOADING]: <ArrowBigUpDash className="icon-row" />,
  [WAITING_APPROVAL]: <Hourglass className="icon-row" />,
  [AUTHORIZED]: <Check className="icon-row" />,
  [TRANSIT]: <ArrowDownUp className="icon-row" />,
  [UNLOADING]: <ArrowBigDownDash className="icon-row" />,
  [FINISHED]: <CheckCheck className="icon-row" />,
  [CANCELED]: <X className="icon-row" />,
}

const allowedEditingStatus = [AUTHORIZED, LOADING, WAITING_APPROVAL]

const BlockTrainDetailSupplier: FC = () => {
  const { id } = useParams()
  const { t } = useTranslation()
  const { blocktrain, blocktrainIsLoading, refetchBlocktrain } = useBlocktrain(id!)
  const { fileTypes } = useBlockTrainFileTypes()
  const originOptions = useOrigins(blocktrain)
  const [addModalIsOpen, setAddModalIsOpen] = useState(false)
  const [finishLoadingModalIsOpen, setFinishLoadingModalIsOpen] = useState<boolean>()
  const [authorizedModalIsOpen, setAuthorizedModalIsOpen] = useState<boolean>()
  const [addFileModalOpen, setAddFileModalIsOpen] = useState(false)
  const [activeKey, setActiveKey] = useState('1')
  const [form] = Form.useForm()
  const [saveDisabled, setSaveDisabled] = useState<boolean>()
  const blocktrainRules = useBlocktrainRules()
  const [selectedRailcar, setSelectedRailcar] = useState<RailcarDetail>()
  const [isModified, setIsModified] = useState(false)
  const { trainCompanies } = useTrainCompanies({})

  const { purchaseOrders, isLoading: purchaseOrdersIsLoading } = useSupplierPurchaseOrders()
  const { materialsOptions } = useMaterials(purchaseOrders)

  const { updateBlocktrain } = useUpdateBlocktrain({
    id: id!,
  })

  const handleDownloadXls = async () => {
    try {
      const blockTrain = await BlocktrainApi.exportAll(id)
      const railCars = blockTrain.railCars ?? []

      const rows = []

      rows.push([`Blocktrain ${id}`])
      rows.push([
        'Block Train',
        'Origin',
        'Destination',
        'Railcar CO:',
        'RailWayBill #:',
        'Railcars #',
        'ETA',
        'ETD',
        'Status',
      ])

      rows.push([
        blockTrain.id,
        blockTrain.origin?.city ?? '',
        blockTrain.destination?.city ?? '',
        blockTrain.trainCompany?.shortName ?? '',
        blockTrain.railCarWayBill ?? '',
        railCars.length,
        blockTrain.estimatedTimeArrival ?? '',
        blockTrain.estimatedDateOfDeparture ?? '',
        blockTrain.status ?? '',
      ])

      rows.push([])

      rows.push(['Railcars'])
      rows.push([
        '#',
        'Railcar ID',
        'Material',
        '# PO Number',
        '# Gross Weight',
        '# Tare Weight',
        '# Net Weight',
        'Delivery Note',
      ])

      railCars.forEach((rc: Railcar, index: number) => {
        const gross = Number(rc.grossTn) ?? 0
        const tare = Number(rc.tareTn) ?? 0
        const net = rc.netTn ?? gross - tare

        rows.push([
          `${blockTrain.id} - ${index + 1}`,
          rc.railCarId ?? '',
          rc.material.nameSap ?? '',
          rc.purchaseOrder?.PurchaseDocumentNumber ?? '',
          gross,
          tare,
          net,
          rc.deliveryNote ?? '',
        ])
      })

      const worksheet = XLSX.utils.aoa_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'BlockTrain')
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
      saveAs(blob, `blocktrain_${blockTrain.id}.xlsx`)
    } catch (error) {
      console.error('Error generating XLS:', error)
    }
  }

  const getBlockingStatus = useCallback(
    (blockingStatus: BlockTrainStatusEnum) => {
      if (fileTypes) {
        return fileTypes.filter(fileType => fileType.blockingStatus === blockingStatus)
      }
      return undefined
    },
    [fileTypes],
  )
  const fileTypesNamesForBlockingNextStatus = useMemo(() => {
    if (blocktrain?.status) {
      const oneNextStatus = STATUS_TRANSITIONS[blocktrain.status]

      if (oneNextStatus) return getBlockingStatus(oneNextStatus)?.map(({ name }) => name)
    }
    return []
  }, [getBlockingStatus, blocktrain?.status])

  const getIsUpdateStatusDisabled = useCallback(() => {
    const filteredFileTypes = fileTypesNamesForBlockingNextStatus
    if (blocktrain && filteredFileTypes) {
      if (!filteredFileTypes.length) {
        return false
      }

      return !filteredFileTypes.every(fileType => {
        return blocktrain.files?.some(file => file.type.name === fileType)
      })
    }
    return true
  }, [blocktrain, fileTypesNamesForBlockingNextStatus])

  const { downloadFile } = useDownload()
  const { allDestinationsOptions } = useDestinations()

  const initialValues = useMemo(
    () => ({
      ...blocktrain,
      origin: blocktrain?.origin.id,
      destination: blocktrain?.destination.id,
      trainCompany: blocktrain?.trainCompany.id,
    }),
    [blocktrain],
  )

  const handleForceReload = () => {
    refetchBlocktrain()
  }

  const actionButtonDownload = (fileData: { key: string; name: string }) => {
    const handleClick = () => {
      downloadFile(fileData)
    }

    return (
      <ActionsCellContainer>
        <Button className="icon-button" onClick={handleClick}>
          <Download />
        </Button>
      </ActionsCellContainer>
    )
  }

  const handleAddBlockTrainFile = async () => {
    try {
      setAddFileModalIsOpen(false)
      form.resetFields()
    } catch (err) {
      console.error('Error uploading file:', err)
    }
  }

  const columnsDocumment: ColumnsType = [
    {
      title: t('number'),
      dataIndex: 'id',
      key: 'docummentId',
      render: (sap_id: string) => sap_id ?? '-',
    },
    {
      title: t('date'),
      dataIndex: 'createAt',
      key: 'fileDate',
      render: (createAt: string) => {
        return createAt ? dayjs(createAt).format('YYYY-MM-DD HH:MM') : '-'
      },
    },
    {
      title: t('type'),
      dataIndex: 'type',
      key: 'documentType',
      render: (type: BlockTrainFileType) => type.name ?? '-',
    },
    {
      title: t('name'),
      dataIndex: 'file',
      key: 'file',
      render: (file: BaseFile) => file?.fileName ?? '-',
    },
    {
      title: t('actions'),
      key: 'commentActions',
      align: 'center',
      dataIndex: 'file',
      className: 'column-actions',
      render: (file: BaseFile) =>
        actionButtonDownload({ key: file.ticketFile, name: file.fileName }),
    },
  ]

  // const columnsComment: ColumnsType = [
  //   {
  //     title: t('number'),
  //     dataIndex: 'id',
  //     key: 'commentId',
  //     render: (commentId: string) => commentId ?? '-',
  // sorter: (a, b) => a.material.nameShort.localeCompare(b.material.nameShort),
  //   },
  //   {
  //     title: t('user'),
  //     dataIndex: 'user',
  //     key: 'commentUser',
  //     render: (user: string) => user ?? '-',
  // sorter: (a, b) => a.material.nameShort.localeCompare(b.material.nameShort),
  //   },
  //   {
  //     title: t('date'),
  //     dataIndex: 'date',
  //     key: 'commentDate',
  //     render: (date: string) => date ?? '-',
  //   },
  //   {
  //     title: t('content'),
  //     dataIndex: 'content',
  //     key: 'content',
  //     render: (content: string) => content ?? '-',
  // sorter: (a, b) => a.material.nameShort.localeCompare(b.material.nameShort),
  //   },
  //   {
  //     title: t('actions'),
  //     key: 'commentActions',
  //     align: 'center',
  //     className: 'column-actions',
  //     render: actionButtonDownload,
  // sorter: (a, b) => a.material.nameShort.localeCompare(b.material.nameShort),
  //   },
  // ]

  const handleTabChange = (key: SetStateAction<string>) => {
    setActiveKey(key)
  }
  const handleOnCancelRailcarModal = () => setAddModalIsOpen(false)

  const handleOnCancelRailcarModalSupplier = () => {
    setSelectedRailcar(undefined)
  }

  // const getIsUpdateStatusDisabled = useCallback((filteredFileTypes?: string[]) => {
  //   if (!blocktrain || !filteredFileTypes) {
  //     return true
  //   }
  //   if (!filteredFileTypes.length) {
  //     return 'validation_error_empty_file'
  //   }
  //   const isDisabled =
  //     !blocktrain.railCars?.length ||
  //     !blocktrain.files?.length ||
  //     !filteredFileTypes.every(fileType =>
  //       blocktrain.files?.some(file => file.type.name === fileType),
  //     )

  //   if (!isDisabled) return false

  //   if (!blocktrain.railCars?.length) {
  //     return 'no_railcars_found'
  //   } else if (!blocktrain.files?.length) {
  //     return 'validation_error_empty_file'
  //   } else if (
  //     !filteredFileTypes.every(fileType =>
  //       blocktrain.files?.some(file => file.type.name === fileType),
  //     )
  //   ) {
  //     return 'missing_required_file_types'
  //   } else {
  //     return false
  //   }

  //   return 'unknown_error'
  // }, [])

  const handleIsModified = () => {
    const { origin, destination, trainCompany, railCarWayBill } = initialValues
    if (
      Object.entries({ origin, destination, trainCompany, railCarWayBill }).some(([key, value]) => {
        return value !== form.getFieldValue(key)
      })
    ) {
      setIsModified(true)
    } else {
      setIsModified(false)
    }
  }

  const handleFieldChange = (
    inputName: keyof typeof initialValues,
    value?: string | number | null,
  ) => {
    handleIsModified()
    if (initialValues[inputName] == value) {
      form.setFields([{ name: inputName, errors: [] }])
    }
  }

  const handleSelectChange =
    (inputName: keyof typeof initialValues) => (value?: string | number | null) =>
      handleFieldChange(inputName, value)

  const handleCancel = () => {
    setIsModified(false)
    form.resetFields()
  }

  const onValidationError = (e: any) => {
    console.error(e)
  }

  const handleOnOk = async () => {
    await form
      .validateFields()
      .then(values => {
        const { origin, destination, railCarWayBill, trainCompany } = values
        const payload: BlocktrainUpdatePayload = {
          origin,
          destination,
          railCarWayBill,
          trainCompany,
        }
        updateBlocktrain(payload)
      })
      .catch(onValidationError)
  }

  const onFormChange = async () => {
    await form
      .validateFields()
      .then(() => {
        setSaveDisabled(false)
      })
      .catch(() => {
        setSaveDisabled(true)
      })
  }

  useEffect(handleCancel, [initialValues])

  return blocktrainIsLoading || !blocktrain ? (
    <LoadingSpinner />
  ) : (
    <ContentContainer>
      <FlexCol
        className="blocktrain-detail__data"
        style={{ padding: '1rem', gap: '0.5rem', marginRight: '0.25rem' }}
      >
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Title level={3}>
            <span>{t('block_train')}</span> <span>{id}</span>
            <Button type="text" icon={<Link />} shape="circle" />
          </Title>
        </header>
        <Form layout="vertical" form={form} initialValues={initialValues} onChange={onFormChange}>
          <FlexRow className="form-flex-row">
            <Form.Item label={t('origin')} name="origin" className="form-item margin-form">
              {blocktrain.status === TRANSIT ||
              blocktrain.status === FINISHED ||
              blocktrain.status === UNLOADING ? (
                <div className="input-like-div">
                  {originOptions.find(option => option.value === blocktrain.origin.id)?.label}
                </div>
              ) : (
                <Select
                  options={originOptions}
                  disabled={!allowedEditingStatus.includes(blocktrain.status)}
                  onChange={handleSelectChange('origin')}
                  onFocus={() => {}}
                />
              )}
            </Form.Item>

            <Form.Item
              label={t('destination')}
              name="destination"
              className="form-item margin-form"
            >
              {blocktrain.status === TRANSIT ||
              blocktrain.status === FINISHED ||
              blocktrain.status === UNLOADING ? (
                <div className="input-like-div">
                  {
                    allDestinationsOptions?.find(
                      option => option.value === blocktrain.destination.id,
                    )?.label
                  }
                </div>
              ) : (
                <Select
                  options={allDestinationsOptions}
                  disabled={!allowedEditingStatus.includes(blocktrain.status)}
                  onChange={handleSelectChange('destination')}
                  onFocus={() => {}}
                />
              )}
            </Form.Item>
            <Form.Item
              label={t('railcar_co')}
              name="trainCompany"
              className="form-item margin-form no-wrap-label "
            >
              {blocktrain.status === TRANSIT ||
              blocktrain.status === FINISHED ||
              blocktrain.status === UNLOADING ? (
                <div className="input-like-div">
                  {
                    trainCompanies?.find(company => company.id === blocktrain.trainCompany.id)
                      ?.shortName
                  }
                </div>
              ) : (
                <Select
                  placeholder={t('select_train_company')}
                  options={trainCompanies?.map(({ id: trainCompanyId, shortName }) => ({
                    label: shortName,
                    value: trainCompanyId,
                  }))}
                  value={blocktrain.trainCompany?.id}
                  disabled={!allowedEditingStatus.includes(blocktrain.status)}
                  onChange={handleSelectChange('trainCompany')}
                />
              )}
            </Form.Item>

            <Form.Item
              label={t('railway_bill_#')}
              name="railCarWayBill"
              className="form-item no-wrap-label"
              rules={blocktrainRules.rayCarWayBill}
            >
              <Input
                className="input-field-railcar"
                disabled={!allowedEditingStatus.includes(blocktrain.status)}
                onChange={({ currentTarget }) =>
                  handleFieldChange('railCarWayBill', currentTarget.value)
                }
              />
            </Form.Item>

            <Form.Item
              label={t('railcars_#')}
              name="railCarQuantity"
              className="form-item no-wrap-label"
            >
              <Input className="input-field-railcar" readOnly />
            </Form.Item>

            <Form.Item label={t('eta')} name="eta" className="form-item margin-form">
              <div className="input-like-div margin-form input-date ">
                {blocktrain?.estimatedDateOfDeparture
                  ? moment(blocktrain.estimatedTimeArrival).format(DATE_KEYS.FORMAT.YEAR_MONTH)
                  : 'N/A'}
              </div>
            </Form.Item>

            <Form.Item label={t('etd')} name="etd" className="form-item margin-form">
              <div className="input-like-div margin-form input-date ">
                {blocktrain?.estimatedDateOfDeparture
                  ? moment(blocktrain.estimatedDateOfDeparture).format(DATE_KEYS.FORMAT.YEAR_MONTH)
                  : 'N/A'}
              </div>
            </Form.Item>
            <Form.Item label={t('status')} name="status" className="form-item margin-form">
              <div className="input-like-div input-status">
                {iconByStatus[blocktrain.status]}{' '}
                <span className="no-wrap-text">{t(`blocktrain_status.${blocktrain.status}`)}</span>
              </div>
            </Form.Item>
          </FlexRow>
        </Form>
        <FlexRow
          style={{
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '-0.25rem',
          }}
        >
          <FlexRow style={{ marginRight: '1rem', display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title={
                blocktrain.railCars?.length
                  ? t('download_data_to_excel')
                  : t('the_download_is_not_available')
              }
              overlayClassName="centered-text-tooltip"
            >
              <Button
                type="primary"
                className="button-style"
                onClick={handleDownloadXls}
                disabled={!blocktrain.railCars?.length}
                style={{
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {t('download_to_excel')}
              </Button>
            </Tooltip>
          </FlexRow>

          <FlexRow style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {isModified ? (
              <>
                <Button
                  onClick={handleCancel}
                  className="form-item button-form button-cancel-save"
                  style={{
                    marginLeft: '0.55rem',
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="primary"
                  disabled={saveDisabled}
                  className="form-item button-form button-cancel-save"
                  onClick={handleOnOk}
                  style={{
                    marginLeft: '0.25rem',
                  }}
                >
                  {t('save')}
                </Button>
              </>
            ) : (
              <>
                {blocktrain.status === BlockTrainStatusEnum.LOADING && (
                  <Button
                    type="primary"
                    className="form-item finish-loading-button button-state"
                    disabled={getIsUpdateStatusDisabled() || blocktrain.railCars?.length === 0}
                    onClick={() => setFinishLoadingModalIsOpen(true)}
                  >
                    {t('finish_loading')}
                    <SquareIcon style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }} />
                  </Button>
                )}
                {blocktrain.status === BlockTrainStatusEnum.AUTHORIZED && (
                  <Button
                    onClick={() => setAuthorizedModalIsOpen(true)}
                    type="primary"
                    className="form-item finish-button button-state"
                    disabled={getIsUpdateStatusDisabled()}
                  >
                    {t('in_transit')}
                    <Train style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }} />
                  </Button>
                )}
              </>
            )}
          </FlexRow>
        </FlexRow>
        {getIsUpdateStatusDisabled() ? (
          <FlexRow style={{ justifyContent: 'flex-end' }}>
            <span style={{ color: 'red', marginBottom: '-2rem' }}>{t('files_missing')}</span>
          </FlexRow>
        ) : (
          blocktrain.railCars?.length === 0 && (
            <FlexRow style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
              <span style={{ color: 'red', marginBottom: '-2rem' }}>{t('no_railcars_found')}</span>
            </FlexRow>
          )
        )}
      </FlexCol>
      <div>
        <Tabs activeKey={activeKey} onChange={handleTabChange}>
          <TabPane key="1" tab={<span className="tabs-offset">{t('railcars_tab')}</span>}>
            {/* //TODO: Add */}
            <RailcarsTabPane
              blocktrain={blocktrain}
              blocktrainsIsLoading={blocktrainIsLoading}
              allowedEditingStatus={allowedEditingStatus}
              setSelectedRailcar={setSelectedRailcar}
              setAddModalIsOpen={setAddModalIsOpen}
              materialsOptions={materialsOptions}
              purchaseOrders={purchaseOrders}
              purchaseOrdersIsLoading={purchaseOrdersIsLoading}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                {t('documents_tab')}
                {getIsUpdateStatusDisabled() && <span style={{ color: 'red' }}>*</span>}
              </span>
            }
            key="2"
          >
            {' '}
            <Table
              columns={columnsDocumment}
              dataSource={blocktrain?.files}
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: <Empty description={t('no_data_found')} />,
              }}
              scroll={{
                x: 'max-content',
              }}
            />
            <div className="div-button">
              {allowedEditingStatus.includes(blocktrain.status) ? (
                <Button
                  type="primary"
                  onClick={() => setAddFileModalIsOpen(true)}
                  className="footer-button"
                  style={{
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {t('add_file')}
                </Button>
              ) : null}
            </div>
          </TabPane>
          {/* <TabPane tab={t('comments_tab')} key="3">
            <Table
              columns={columnsComment}
              dataSource={[]}
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: <Empty description={t('no_data_found')} />,
              }}
            />
          </TabPane> */}
        </Tabs>
      </div>
      {addModalIsOpen && (
        <AddRailcarModal
          visible={addModalIsOpen}
          onCancel={handleOnCancelRailcarModal}
          blockTrain={blocktrain}
          materialsOptions={materialsOptions}
          purchaseOrders={purchaseOrders}
        />
      )}
      <AddBlockTrainFileModal
        visible={addFileModalOpen}
        onCancel={() => setAddFileModalIsOpen(false)}
        onCreate={handleAddBlockTrainFile}
        blockTrainStatus={blocktrain!.status}
        blockTrainId={blocktrain!.id}
        fileTypes={fileTypes}
      />
      {!blocktrainIsLoading && selectedRailcar && blocktrain && (
        <RailcarModalViewSupplier
          key={selectedRailcar.id}
          onCancel={handleOnCancelRailcarModalSupplier}
          blockTrain={blocktrain}
          railcar={selectedRailcar}
          onOk={handleOnOk}
          reloadBlocktrain={handleForceReload}
        />
      )}
      <ConfirmFinishLoading
        visible={!!finishLoadingModalIsOpen}
        close={() => {
          setFinishLoadingModalIsOpen(false)
        }}
      />
      <ConfirmAuthorized
        visible={!!authorizedModalIsOpen}
        close={() => {
          setAuthorizedModalIsOpen(false)
        }}
        blocktrain={blocktrain}
      />
    </ContentContainer>
  )
}

export default BlockTrainDetailSupplier
